import {
  getTreatments as getSplitIoTreatments,
  initSplit as initSplitIo,
  isSplitIoInitiated as selectIsSplitIoInitiated,
} from '@travel-frontend/split-io';
import {
  injectGTMSnippet,
  useWishlistMetaSyncEffect,
} from '@travel-frontend/ui-components';
import {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useSearchParams as useUrlParams} from 'react-router';

import {config, defaultSearchParamsByTravelType, splitNames} from '@/config';
import {SPLIT_OVERRIDE_URL_PARAM} from '@/consts';
import {useLiveChat} from '@/modules/common/components/useLiveChat';
import {useBloomreach} from '@/modules/common/context/Bloomreach';
import {useConsent} from '@/modules/common/context/Consent';
import {useLocalSplit} from '@/modules/common/hooks';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useFilterParams} from '@/modules/common/hooks/useFilterParams';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';
import {useTracking} from '@/modules/common/hooks/useTracking';
import {useAppSelector} from '@/redux/config';
import {fetchKeywordData} from '@/redux/v1/keywordData/actions';

export const GlobalSideEffects = () => {
  const [gtmInit, setGtmInit] = useState(false);
  const dispatch = useDispatch();
  const currentPage = useCurrentPage();
  const {trackPageImpression} = useTracking();
  const bloomreach = useBloomreach();
  const [urlParams] = useUrlParams();
  const consent = useConsent();
  const preferencesConsentGiven = consent.status?.preferences === true;

  const isSplitIoReady = useAppSelector(selectIsSplitIoInitiated);
  const splitOverrideParam = urlParams.getAll(SPLIT_OVERRIDE_URL_PARAM);

  const [searchParams] = useSearchParams();
  const [filterParams] = useFilterParams();
  const defaultSearchParams =
    defaultSearchParamsByTravelType[searchParams.travelType];
  const {destinationId} = searchParams;

  useEffect(() => {
    if (!gtmInit) {
      return;
    }

    const key = import.meta.env.VITE_SERVICE_SPLITIO_KEY;
    const url = import.meta.env.VITE_SERVICE_SPLITIO_URL;
    const urlSettings =
      url === 'DEFAULT'
        ? undefined
        : // We preconnect to the splitio api in index.html. For this we need the base url.
          // To avoid having another env var, we simply add the "/api" path here.
          {sdk: `${url}/api`, events: `${url}/api`, auth: `${url}/api`};

    dispatch(
      // @ts-expect-error ...
      initSplitIo(key, urlSettings)
    );
  }, [dispatch, gtmInit]);

  // writes split.io treatments to redux store
  useEffect(() => {
    if (isSplitIoReady) {
      dispatch(
        // @ts-expect-error ...
        getSplitIoTreatments(
          {
            splitNames,
          },
          {
            s: splitOverrideParam,
          }
        )
      );
    }
  }, [isSplitIoReady, splitOverrideParam, dispatch]);

  // fetch keyword-data for "destinationId"-parameter so that we can show the resolved name in a bunch of places
  useEffect(() => {
    if (destinationId) {
      // @ts-expect-error ...
      dispatch(fetchKeywordData(destinationId));
    }
  }, [destinationId, dispatch]);

  // page-view/load tracking
  useEffect(
    () => {
      setGtmInit(true);
      trackPageImpression({
        page: currentPage,
        searchParams,
        filterParams,
        defaultSearchParams,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omitting `searchParams` and `filterParams` here, since the effect is only supposed to fire when `currentPage` changes
    [currentPage, defaultSearchParams, trackPageImpression]
  );

  // Begin: Bloomreach push notifications
  const bloomreachPushNotificationsEnabled =
    useLocalSplit('FFP_4024_bloomreach_push_notifications').treatment === 'on';

  // ...
  const [
    shouldShowPushNotificationPrompt,
    setShouldShowPushNotificationPrompt,
  ] = useState(false);

  useEffect(() => {
    if (preferencesConsentGiven) {
      bloomreach.initSdkOnce(config.bloomreach);
    }
  }, [bloomreach, preferencesConsentGiven]);

  useEffect(() => {
    if (preferencesConsentGiven && bloomreach.api) {
      bloomreach.api.start(config.bloomreach);
    }
  }, [preferencesConsentGiven, bloomreach.api]);

  useEffect(() => {
    if (
      preferencesConsentGiven &&
      bloomreachPushNotificationsEnabled &&
      bloomreach.api
    ) {
      // Nesting api calls doesn't work reliably for some reason (ideally we would call "subscribe()" inside "isSubscribed()").
      // My guess it that it has to be called in a user initiated event-handler?
      bloomreach.api.notifications.isSubscribed((active, denied) => {
        // So we store the current subscription status in state instead,
        // so that we can react to it changing (see useEffect below).
        setShouldShowPushNotificationPrompt(!active && !denied);
      });
    }
  }, [
    preferencesConsentGiven,
    bloomreachPushNotificationsEnabled,
    bloomreach.api,
  ]);

  useEffect(() => {
    if (bloomreach.api && shouldShowPushNotificationPrompt) {
      // show the browser push notification prompt
      bloomreach.api.notifications.subscribe();
    }
  }, [bloomreach.api, shouldShowPushNotificationPrompt]);
  // End: Bloomreach push notifications

  // order is important here!
  // page impression has to be pushed into dataLayer first before we can load GTM
  // (because some GA tags require this information in order to be executed correctly on "gtm.start")
  // Also bloomreach snippet must come before, because there are some gtm-tags that start calling the api right away.
  useEffect(() => {
    injectGTMSnippet(
      import.meta.env.VITE_SERVICE_GOOGLE_TAG_MANAGER_ID,
      import.meta.env.VITE_SERVICE_GOOGLE_TAG_MANAGER_URL
    );
  }, []);

  useLiveChat();

  const isFavoriteEnabled =
    useLocalSplit('FPO_6430_wishlist_feature_toggle').treatment === 'on';

  useWishlistMetaSyncEffect({isEnabled: isFavoriteEnabled});

  return null;
};
